import type { StyleSystemProps } from '@withjoy/joykit/styled-system';
import { styled } from '@withjoy/joykit/theme';
import { Box } from '../Box';
import { anatomy } from '../../common/utils/anatomy';
import type { MultiPartStyleConfig, PartsStyleFunction } from '../../common/utils/styleConfig';
import type { BadgeProps, BadgeVariant, BadgeIntent, BadgeSize } from './Badge.types';

// ============================================================================
// Anatomy
// ============================================================================

export const badgeAnatomy = anatomy('Badge').parts('root', 'icon', 'label');

// ============================================================================
// Styled components
// ============================================================================

export const StyledRoot = styled(Box)``;
export const StyledIcon = styled(Box)``;
export const StyledLabel = styled(Box)``;

// ============================================================================
// Default props
// ============================================================================

type BadgeDefaultProps = Required<Pick<BadgeProps, 'variant' | 'intent' | 'size'>>;

export const BADGE_DEFAULT_PROPS: BadgeDefaultProps = {
  variant: 'label',
  intent: 'primary',
  size: 'md',
} as const;

// ============================================================================
// Intent color mappings
// ============================================================================

type IntentColors = Readonly<{ bg: string; text: string; border: string; outlineText: string }>;

const intentColorMap: ReadonlyRecord<BadgeIntent, IntentColors> = {
  primary: { bg: 'newUIBrandPrimary', text: 'white', border: 'newUIBrandPrimary', outlineText: 'newUIBrandPrimary' },
  success: { bg: 'positive6', text: 'white', border: 'positive6', outlineText: 'positive6' },
  destructive: { bg: 'negative6', text: 'white', border: 'negative6', outlineText: 'negative6' },
  warning: { bg: 'warning6', text: 'white', border: 'warning6', outlineText: 'warning6' },
  neutral: { bg: 'mono3', text: 'mono14', border: 'mono5', outlineText: 'mono14' },
};

// ============================================================================
// Size styles
// ============================================================================

type SizeTokens = Readonly<{
  height: string;
  typographyVariant: 'label3' | 'label4';
  counterMinWidth: string;
}>;

const sizeMap: ReadonlyRecord<BadgeSize, SizeTokens> = {
  sm: { height: '20px', typographyVariant: 'label4', counterMinWidth: '20px' },
  md: { height: '24px', typographyVariant: 'label3', counterMinWidth: '24px' },
};

// ============================================================================
// Glass intent tints — light-2 palette shade at 60% opacity
// ============================================================================

type GlassColors = Readonly<{ bg: string; text: string }>;

const GLASS_BORDER = 'rgba(255, 255, 255, 0.5)';

const glassColorMap: ReadonlyRecord<BadgeIntent, GlassColors> = {
  primary: { bg: 'rgba(238, 235, 255, 0.6)', text: 'newUIBrandPrimary' },
  success: { bg: 'rgba(232, 255, 248, 0.6)', text: 'positive6' },
  destructive: { bg: 'rgba(255, 229, 234, 0.6)', text: 'negative6' },
  warning: { bg: 'rgba(255, 243, 229, 0.6)', text: 'warning6' },
  neutral: { bg: 'rgba(247, 247, 247, 0.6)', text: 'mono14' },
};

// ============================================================================
// Variant-specific root styles
// ============================================================================

const getVariantRootStyles = (variant: BadgeVariant, intent: BadgeIntent): StyleSystemProps => {
  const colors = intentColorMap[intent];

  switch (variant) {
    case 'outline':
      return {
        backgroundColor: 'transparent',
        border: '1px solid',
        borderColor: colors.border,
      };
    case 'glass':
      return {
        backgroundColor: glassColorMap[intent].bg,
        border: '1px solid',
        borderColor: GLASS_BORDER,
        backdropFilter: 'blur(24px)',
      };
    case 'counter':
      return {
        backgroundColor: colors.bg,
      };
    case 'label':
    case 'status':
    // Visually identical — the distinction is purely semantic (consumer intent signaling)
    default:
      return {
        backgroundColor: colors.bg,
      };
  }
};

// ============================================================================
// Style config
// ============================================================================

type BadgeStyleProps = BadgeProps & BadgeDefaultProps & { isSingleDigitCounter?: boolean; hasIcon?: boolean };

const ICON_SIZE = '16px';

const badgeBaseStyle: PartsStyleFunction<BadgeStyleProps, typeof badgeAnatomy> = props => {
  const { variant, intent, size, isSingleDigitCounter, hasIcon } = props;
  const sizeTokens = sizeMap[size];
  const colors = intentColorMap[intent];

  const textColor = variant === 'glass' ? glassColorMap[intent].text : variant === 'outline' ? colors.outlineText : colors.text;

  const isCounter = variant === 'counter';
  const counterWidth = isCounter && isSingleDigitCounter ? sizeTokens.counterMinWidth : undefined;

  return {
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '9999px',
      width: counterWidth ?? 'fit-content',
      minWidth: isCounter ? sizeTokens.counterMinWidth : undefined,
      height: sizeTokens.height,
      paddingX: isCounter && isSingleDigitCounter ? 0 : 3,
      gap: hasIcon ? '4px' : undefined,
      lineHeight: 1,
      flexShrink: 0,
      ...getVariantRootStyles(variant, intent),
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: ICON_SIZE,
      height: ICON_SIZE,
      flexShrink: 0,
      color: textColor,
    },
    label: {
      typographyVariant: sizeTokens.typographyVariant,
      color: textColor,
      fontWeight: 600,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      textAlign: 'center',
    },
  };
};

export const badgeStyles: MultiPartStyleConfig<BadgeStyleProps, typeof badgeAnatomy> = {
  parts: badgeAnatomy.keys,
  baseStyle: badgeBaseStyle,
  defaultProps: BADGE_DEFAULT_PROPS,
};
